// 关于页数据标准化
import { resolveFileUrl } from '@/components/shared/utils/fileUrl'
import { resolveBodyHtml } from '@/components/shared/utils/resolveBodyHtml'
import { normalizeContentAttachments } from '@/components/shared/utils/contentAttachments'
import { parseShowAttachments } from '@/components/shared/utils/contentDisplayFlags'
import {
  getMenuNodePath,
  slugFromMenuPath
} from '@/components/portal/common/utils/menuPath'
import { resolveTemplateId } from '@/components/portal/common/utils/menuTemplateRegistry'
import { isPortalMenuVisible } from '@/components/portal/common/utils/portalMenuVisibility'

/** 首页公司简介区块展示的三个子页面 slug（顺序固定） */
export const HOME_ABOUT_SLUGS = ['profile-values', 'profile', 'profile-subsidiary']

export const ABOUT_ICON_BY_SLUG = {
  profile: 'building',
  history: 'clock',
  responsibility: 'heart',
  technology: 'chip',
  business: 'globe',
  certification: 'shield'
}

const FALLBACK_ICONS = ['building', 'clock', 'heart', 'chip', 'globe', 'shield']

function findAboutMenu(menus = [], rootMenu = null) {
  if (rootMenu) return rootMenu
  return (menus || []).find((menu) => (
    isPortalMenuVisible(menu)
    && resolveTemplateId(menu.templateId) === 3
  )) || (menus || []).find((menu) => (
    isPortalMenuVisible(menu)
    && menu.contentType === 'INTRODUCTION'
    && menu.children?.length
  )) || null
}

/** 构建关于我们 menuId → 元数据 的扁平映射表 */
export function buildAboutMenuMetaMap(menus = [], rootMenu = null) {
  const map = new Map()
  const aboutMenu = findAboutMenu(menus, rootMenu)
  if (!aboutMenu) return map

  const walk = (nodes = [], parentSlug = '', level = 2) => {
    for (const node of nodes) {
      if (!isPortalMenuVisible(node)) continue

      const slug = slugFromMenuPath(node.path) || String(node.id)
      map.set(node.id, {
        menuId: node.id,
        slug,
        path: getMenuNodePath(node),
        name: node.name,
        parentSlug,
        level,
        sortOrder: node.sortOrder ?? 0
      })

      if (node.children?.length) {
        walk(node.children, slug, level + 1)
      }
    }
  }

  walk(aboutMenu.children || [])
  return map
}

/** 概况网格卡片（菜单分类：名称、卡片背景图，回退 banner / 内容封面） */
export function buildAboutCategoryCards(rootMenu, items = []) {
  if (!rootMenu) return []

  const itemBySlug = (slug) => (items || []).find((entry) => entry.slug === slug) || null

  return (rootMenu.children || [])
    .filter((child) => isPortalMenuVisible(child))
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((child) => {
      const slug = slugFromMenuPath(child.path) || String(child.id)
      const path = getMenuNodePath(child)
      const item = itemBySlug(slug)
      const cardImageUrl =
        resolveFileUrl(child.cardImageUrl)
        || resolveFileUrl(child.bannerUrl)
        || item?.image
        || ''

      const children = (child.children || [])
        .filter((level3) => isPortalMenuVisible(level3))
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map((level3) => {
          const childSlug = slugFromMenuPath(level3.path) || String(level3.id)
          const childItem = itemBySlug(childSlug)
          return {
            slug: childSlug,
            path: getMenuNodePath(level3),
            name: childItem?.title || level3.name
          }
        })

      return {
        slug,
        path: path || item?.path || '',
        title: item?.title || child.name || '',
        cardImageUrl,
        children
      }
    })
}

/** 构建关于我们二级/三级菜单树结构 */
export function buildAboutMenuTree(menus = [], rootMenu = null) {
  const aboutMenu = findAboutMenu(menus, rootMenu)
  if (!aboutMenu) return []

  return (aboutMenu.children || [])
    .filter((item) => isPortalMenuVisible(item))
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((level2) => {
      const slug = slugFromMenuPath(level2.path) || String(level2.id)
      const children = (level2.children || [])
        .filter((item) => isPortalMenuVisible(item))
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map((level3) => ({
          menuId: level3.id,
          slug: slugFromMenuPath(level3.path) || String(level3.id),
          path: getMenuNodePath(level3),
          name: level3.name,
          parentSlug: slug,
          sortOrder: level3.sortOrder ?? 0
        }))

      return {
        menuId: level2.id,
        slug,
        path: getMenuNodePath(level2),
        name: level2.name,
        cardImageUrl: resolveFileUrl(level2.cardImageUrl) || resolveFileUrl(level2.bannerUrl) || '',
        sortOrder: level2.sortOrder ?? 0,
        children
      }
    })
}

/** 标准化单条关于我们列表项 */
export function normalizeAboutItem(content, menuMap) {
  const menu = menuMap.get(content.menuId) || {}
  const slug = menu.slug || String(content.menuId || content.id)

  return {
    id: content.id,
    menuId: content.menuId,
    slug,
    path: menu.path || '',
    parentSlug: menu.parentSlug || '',
    level: menu.level ?? 2,
    title: content.title || menu.name || '',
    summary: content.summary || '',
    image: resolveFileUrl(content.coverUrl),
    sortOrder: menu.sortOrder ?? content.sortOrder ?? 0
  }
}

/** 批量标准化关于我们内容列表 */
export function normalizeAbouts(contents = [], menus = [], rootMenu = null) {
  const menuMap = buildAboutMenuMetaMap(menus, rootMenu)

  return (contents || [])
    .filter((item) => item.type === 'INTRODUCTION')
    .map((item) => normalizeAboutItem(item, menuMap))
    .sort((a, b) => a.sortOrder - b.sortOrder)
}

/** 标准化关于我们详情数据 */
export function normalizeAboutDetail(content, listItem = null) {
  if (!content) return null

  return {
    id: content.id,
    menuId: content.menuId,
    slug: listItem?.slug || String(content.menuId || content.id),
    path: listItem?.path || '',
    parentSlug: listItem?.parentSlug || '',
    title: content.title || listItem?.title || '',
    summary: content.summary || listItem?.summary || '',
    image: resolveFileUrl(content.coverUrl) || listItem?.image || '',
    content: resolveBodyHtml(content.bodyHtml, content.bodyJson),
    attachments: normalizeContentAttachments(content.attachments),
    showAttachments: parseShowAttachments(content),
    sortOrder: listItem?.sortOrder ?? content.sortOrder ?? 0
  }
}

/** 收集菜单树与内容项中所有有效的 about slug */
export function collectAboutSlugs(menuTree = [], items = []) {
  const slugs = new Set()

  for (const category of menuTree) {
    if (category.slug) slugs.add(category.slug)
    for (const child of category.children || []) {
      if (child.slug) slugs.add(child.slug)
    }
  }

  for (const item of items) {
    if (item.slug) slugs.add(item.slug)
    if (item.id != null) slugs.add(String(item.id))
  }

  return slugs
}

/** 根据 slug 解析关于我们卡片图标名，无匹配时按索引回退 */
export function resolveAboutIcon(slug, index = 0) {
  return ABOUT_ICON_BY_SLUG[slug] || FALLBACK_ICONS[index % FALLBACK_ICONS.length]
}

/** 根据 slug 在菜单树中查找路径 */
export function findAboutPathBySlug(menuTree = [], slug) {
  if (!slug) return ''
  for (const category of menuTree) {
    if (category.slug === slug && category.path) return category.path
    for (const child of category.children || []) {
      if (child.slug === slug && child.path) return child.path
    }
  }
  return ''
}
